import { useGetAllDepartmentsQuery } from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
import {
  useGetAdminKPIPlanDetailsQuery,
  useCreateKPIAppraisalMutation,
} from "@/app/features/employee/performance/performance.api";
import type { CustomModalProps } from "@/components/CustomModal";
import { Calendar03Icon, PencilEdit02Icon } from "@/icons";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { DATE_FORMATS, formatDate } from "@/utils";
import { Button, DatePicker, Divider, Flex, Form, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import CustomDrawer from "../../../../../components/CustomDrawer";
const { Text } = Typography;

/** Default gap between a department's start date and its due date. */
const DEFAULT_DUE_OFFSET_MONTHS = 1;

/** Due date defaults to one month after the given start date. */
const getDefaultDueDate = (start: dayjs.Dayjs) =>
  start.add(DEFAULT_DUE_OFFSET_MONTHS, "month");

type FormType = {
  bulk_appraisal_start_date?: dayjs.Dayjs;
  bulk_due_date?: dayjs.Dayjs;
  departments: {
    department_id: string;
    name: string;
    appraisal_start_date: dayjs.Dayjs;
    due_date: dayjs.Dayjs;
    customized: boolean;
  }[];
};

type ManageAppraisalProps = {
  open?: boolean;
  handleClose: () => void;
  id?: string;
  type?: string;
} & CustomModalProps;

const ManageAppraisal = ({
  open,
  handleClose,
  id,
  type,
}: ManageAppraisalProps) => {
  const [form] = Form.useForm<FormType>();
  const isView = type === "VIEW";
  const isUpdate = type === "UPDATE";

  const { data: deptData } = useGetAllDepartmentsQuery({});
  const { data: detailsData } = useGetAdminKPIPlanDetailsQuery(id as string, {
    skip: !id,
  });

  const departments = Form.useWatch("departments", form);
  const bulkStartDate = Form.useWatch("bulk_appraisal_start_date", form);

  useEffect(() => {
    if (open && detailsData?.data) {
      const data = detailsData.data;
      form.setFieldsValue({
        bulk_appraisal_start_date: dayjs(),
        bulk_due_date: dayjs(),
        departments: (data.departmentMappings || []).map((dm: any) => {
          const dept = (deptData?.data?.data || []).find(
            (d: any) => d.id === dm.departmentId,
          );
          return {
            department_id: dm.departmentId,
            name: dept?.name || "n/a",
            appraisal_start_date: dm.appraisalStartDate
              ? dayjs(dm.appraisalStartDate)
              : dayjs(),
            due_date: dm.appraisalDueDate
              ? dayjs(dm.appraisalDueDate)
              : dayjs(),
            customized: false,
          };
        }),
      });
    }
  }, [open, detailsData, deptData, form]);

  // Update and create share the same endpoint; UPDATE mode only differs by
  // pre-filling defaults from the plan details and by its labels.
  const [createKPIAppraisal, { isLoading: isSubmitting }] =
    useCreateKPIAppraisalMutation();

  const onFinish = async ({
    bulk_appraisal_start_date,
    bulk_due_date,
    ...values
  }: FormType) => {
    if (isView) return;

    const action = isUpdate ? "update" : "open";
    try {
      const payload = {
        plan_id: id || "",
        departments: (values.departments || []).map((d) => ({
          department_id: d.department_id,
          appraisal_start_date: formatDate(
            d.appraisal_start_date,
            DATE_FORMATS.ISO_DATE,
          ),
          due_date: formatDate(d.due_date, DATE_FORMATS.ISO_DATE),
        })),
      };

      const res = await createKPIAppraisal(payload).unwrap();

      if (res?.success === false) {
        toast("error", getErrorMessage(res, `Failed to ${action} Appraisal`));
        return;
      }

      toast(
        "success",
        isUpdate
          ? "Appraisal updated successfully!"
          : "Appraisal opened successfully!",
      );
      onClose();
    } catch (error: any) {
      toast("error", getErrorMessage(error, `Failed to ${action} Appraisal`));
    }
  };

  const onClose = () => {
    handleClose();
    form.resetFields();
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={onClose}
      title={
        isView
          ? "View Appraisal"
          : isUpdate
            ? "Update Appraisal"
            : "Open Appraisal"
      }
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        onFinish={onFinish}
        className="custom-form space-y-6 pb-6"
        onValuesChange={(changedValues, allValues) => {
          if (changedValues.bulk_appraisal_start_date) {
            const start = changedValues.bulk_appraisal_start_date;
            const due = getDefaultDueDate(start);
            const currentDepts = allValues.departments || [];
            form.setFieldsValue({
              bulk_due_date: due,
              departments: currentDepts.map((d: any) => ({
                ...d,
                appraisal_start_date: start,
                due_date: due,
                customized: false,
              })),
            });
          }
          if (changedValues.bulk_due_date) {
            const date = changedValues.bulk_due_date;
            const currentDepts = allValues.departments || [];
            form.setFieldsValue({
              departments: currentDepts.map((d: any) => ({
                ...d,
                due_date: date,
                customized: false,
              })),
            });
          }
        }}
      >
        <Flex vertical gap={12} className="mb-3">
          <Flex justify="space-between">
            <Text className="text-theme-gray-400">Name</Text>
            <Text className="text-white font-medium text-lg">
              {detailsData?.data?.planName || "n/a"}
            </Text>
          </Flex>
          <Flex justify="space-between">
            <Text className="text-theme-gray-400">Plan Type</Text>
            <Text className="text-white font-medium text-lg">
              {detailsData?.data?.planType || "n/a"}
            </Text>
          </Flex>
          <Flex justify="space-between">
            <Text className="text-theme-gray-400">Plan Period</Text>
            <Text className="text-white font-medium text-lg">
              {detailsData?.data?.planPeriod || "n/a"}
            </Text>
          </Flex>
          <Flex justify="space-between">
            <Text className="text-theme-gray-400">Cycle</Text>
            <Text className="text-white font-medium text-lg">
              {detailsData?.data?.startDate
                ? dayjs(detailsData.data.startDate).format("DD/MM/YYYY")
                : "n/a"}{" "}
              -{" "}
              {detailsData?.data?.endDate
                ? dayjs(detailsData.data.endDate).format("DD/MM/YYYY")
                : "n/a"}
            </Text>
          </Flex>
        </Flex>

        <Divider className="border-theme-gray-800 my-3" />

        <div className="space-y-6">
          <Flex
            justify="space-between"
            align="center"
            className="mb-4 border-b border-theme-gray-800 pb-3"
          >
            <Text className="text-white font-semibold text-base">
              Department
            </Text>
            {isView ? (
              <div className="flex gap-2 pr-1 opacity-70">
                <Flex gap={5}>
                  <Calendar03Icon size={18} className="text-theme-primary" />
                  <Text className="text-white font-medium">
                    {dayjs().format("DD/MM/YYYY")}
                  </Text>
                </Flex>
              </div>
            ) : (
              <Flex gap={16} align="center" className="shrink-0">
                <Flex vertical gap={2} className="w-[150px]">
                  <Text className="text-theme-gray-400 text-xs">
                    Start Date
                  </Text>
                  <Form.Item name="bulk_appraisal_start_date" noStyle>
                    <DatePicker
                      picker="date"
                      inputReadOnly
                      allowClear={false}
                      format="DD/MM/YYYY"
                      className="p-0 border-none !bg-transparent text-white font-medium cursor-pointer w-full"
                      disabledDate={(current) =>
                        current && current < dayjs().startOf("day")
                      }
                      suffixIcon={
                        <PencilEdit02Icon
                          size={18}
                          className="text-theme-primary cursor-pointer"
                        />
                      }
                    />
                  </Form.Item>
                </Flex>
                <Flex vertical gap={2} className="w-[150px]">
                  <Text className="text-theme-gray-400 text-xs">Due Date</Text>
                  <Form.Item name="bulk_due_date" noStyle>
                    <DatePicker
                      picker="date"
                      inputReadOnly
                      allowClear={false}
                      format="DD/MM/YYYY"
                      className="p-0 border-none !bg-transparent text-white font-medium cursor-pointer w-full"
                      disabledDate={(current) =>
                        current &&
                        current <
                          (bulkStartDate
                            ? dayjs(bulkStartDate).startOf("day")
                            : dayjs().startOf("day"))
                      }
                      suffixIcon={
                        <PencilEdit02Icon
                          size={18}
                          className="text-theme-primary cursor-pointer"
                        />
                      }
                    />
                  </Form.Item>
                </Flex>
              </Flex>
            )}
          </Flex>

          {isView ? (
            <div className="space-y-5">
              {(departments || []).map((dept, index) => (
                <Flex key={index} justify="space-between" align="center">
                  <Text className="text-theme-gray-300 text-[15px]">
                    {dept?.name}
                  </Text>
                  <Flex align="center" gap={16}>
                    <Flex
                      align="center"
                      gap={6}
                      justify="start"
                      className="w-[150px]"
                    >
                      <Calendar03Icon
                        size={18}
                        className="text-theme-primary"
                      />
                      <Text className="text-theme-gray-300 font-medium">
                        {dept?.appraisal_start_date
                          ? dayjs(dept.appraisal_start_date).format(
                              "DD/MM/YYYY",
                            )
                          : "n/a"}
                      </Text>
                    </Flex>
                    <Flex
                      align="center"
                      gap={6}
                      justify="start"
                      className="w-[150px]"
                    >
                      <Calendar03Icon
                        size={18}
                        className="text-theme-primary"
                      />
                      <Text className="text-theme-gray-300 font-medium">
                        {dept?.due_date
                          ? dayjs(dept.due_date).format("DD/MM/YYYY")
                          : "n/a"}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              ))}
            </div>
          ) : (
            <Form.List name="departments">
              {(fields) => (
                <div className="space-y-5 max-h-[400px] overflow-y-auto custom-scrollbar">
                  {fields.map(({ key, name }) => {
                    const dept = departments?.[name];

                    return (
                      <Flex key={key} justify="space-between" align="center">
                        <Form.Item
                          name={[name, "customized"]}
                          initialValue={false}
                          hidden
                        />
                        <Text className="text-theme-gray-300 text-[15px]">
                          {dept?.name}
                        </Text>
                        <Flex gap={16} align="center" className="shrink-0 pr-3">
                          <div className="w-[150px]">
                            <Form.Item
                              noStyle
                              className="w-auto"
                              name={[name, "appraisal_start_date"]}
                            >
                              <DatePicker
                                picker="date"
                                inputReadOnly
                                allowClear={false}
                                format="DD/MM/YYYY"
                                className="p-0 border-none !bg-transparent"
                                disabledDate={(current) =>
                                  current && current < dayjs().startOf("day")
                                }
                                onChange={(date) => {
                                  // Changing the start date resets the due date
                                  // to the default one-month offset.
                                  form.setFields([
                                    {
                                      name: ["departments", name, "due_date"],
                                      value: date
                                        ? getDefaultDueDate(date)
                                        : undefined,
                                    },
                                    {
                                      name: ["departments", name, "customized"],
                                      value: true,
                                    },
                                  ]);
                                }}
                                suffixIcon={
                                  <PencilEdit02Icon
                                    size={18}
                                    className="text-theme-primary cursor-pointer"
                                  />
                                }
                                prefix={
                                  <Calendar03Icon
                                    size={18}
                                    className="text-theme-primary"
                                  />
                                }
                              />
                            </Form.Item>
                          </div>
                          <div className="w-[150px]">
                            <Form.Item
                              noStyle
                              className="w-auto"
                              name={[name, "due_date"]}
                            >
                              <DatePicker
                                picker="date"
                                inputReadOnly
                                allowClear={false}
                                format="DD/MM/YYYY"
                                className="p-0 border-none !bg-transparent"
                                disabledDate={(current) =>
                                  current &&
                                  current <
                                    (dept?.appraisal_start_date
                                      ? dayjs(
                                          dept.appraisal_start_date,
                                        ).startOf("day")
                                      : dayjs().startOf("day"))
                                }
                                onChange={() => {
                                  form.setFieldValue(
                                    ["departments", name, "customized"],
                                    true,
                                  );
                                }}
                                suffixIcon={
                                  <PencilEdit02Icon
                                    size={18}
                                    className="text-theme-primary cursor-pointer"
                                  />
                                }
                                prefix={
                                  <Calendar03Icon
                                    size={18}
                                    className="text-theme-primary"
                                  />
                                }
                              />
                            </Form.Item>
                          </div>
                        </Flex>
                      </Flex>
                    );
                  })}
                </div>
              )}
            </Form.List>
          )}
        </div>

        <div className="flex gap-4 pt-4 justify-end">
          <Button
            size="large"
            className="btn-primary-bordered min-w-[100px]"
            onClick={onClose}
          >
            Cancel
          </Button>
          {!isView && (
            <Button
              size="large"
              className="btn-primary min-w-[100px]"
              htmlType="submit"
              loading={isSubmitting}
            >
              {isUpdate ? "Update" : "Open"}
            </Button>
          )}
        </div>
      </Form>
    </CustomDrawer>
  );
};

export default ManageAppraisal;
